import { useTranslation } from "../../context/TranslationContext";
import { APP_STORE_PAGES, CATEGORY_KEYS, SINGLE_COPY_APP_IDS } from "../../data/appStorePages";
import { AppScope } from "../ds/AppCover";
import AppCover from "../ds/AppCover";
import AppIcon, { APP_COLORS, appKey } from "../ds/AppIcon";
import Button from "../ds/Button";
import Icon from "../ds/Icon";

const Divider = () => <div className="h-px bg-separator-default" aria-hidden="true" />;

/** Figma "Preview · Product": the app's product as buyers meet it. Shown, not used. */
const ProductPreview = ({ app, text, spaceName }) => {
  const colors = APP_COLORS[appKey(app.id)];
  return (
    <div className="flex min-w-0 flex-col overflow-hidden rounded-radius-lg bg-bg-raised">
      <div className="relative h-[88px] shrink-0">
        <AppCover app={app.id} />
      </div>
      <div className="flex flex-col gap-[10px] p-space-16">
        <div className="flex items-center gap-[10px]">
          <span className={`flex size-9 shrink-0 items-center justify-center rounded-[12px] ${colors.deep} ${colors.label}`}>
            <Icon name="message" size={18} />
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
            <p className="truncate text-headline text-label-primary">{text("productTitle")}</p>
            <p className="truncate text-footnote text-label-tertiary">
              {spaceName} · {app.storeName}
            </p>
          </div>
        </div>
        <div className="flex items-center justify-between gap-space-8">
          <p className="min-w-0 truncate text-title-3 text-label-primary">{text("productPrice")}</p>
          <Button size="m" tabIndex={-1} className="pointer-events-none">
            {text("productAction")}
          </Button>
        </div>
      </div>
    </div>
  );
};

/** Figma "Preview · After payment": what happens the moment they've paid. */
const AfterPreview = ({ app, text }) => {
  const colors = APP_COLORS[appKey(app.id)];
  return (
    <div className="flex min-w-0 flex-col items-center gap-[14px] rounded-radius-lg bg-bg-raised p-space-20">
      <span className={`flex size-12 shrink-0 items-center justify-center rounded-full ${colors.base} ${colors.on}`}>
        <Icon name="check" size={24} />
      </span>
      <p className="text-center text-headline text-label-primary">{text("afterTitle")}</p>
      <p className="text-center text-subheadline text-label-secondary">{text("afterBody")}</p>
      <span className="h-[6px] w-full overflow-hidden rounded-full bg-fill-tertiary">
        <span className={`block h-full w-[65%] rounded-[3px] ${colors.base}`} />
      </span>
      <span className={`inline-flex max-w-full items-center gap-space-8 rounded-full px-space-12 py-space-8 ${colors.deep}`}>
        <Icon name="link" size={14} className={colors.label} />
        <span className={`truncate text-footnote ${colors.label}`}>{text("afterLink")}</span>
      </span>
    </div>
  );
};

const Feature = ({ app, icon, title, body }) => {
  const colors = APP_COLORS[appKey(app.id)];
  return (
    <div className="flex gap-space-16 py-space-16">
      <span className={`flex size-9 shrink-0 items-center justify-center rounded-[12px] ${colors.deep} ${colors.label}`}>
        <Icon name={icon} size={18} />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-space-4">
        <h3 className="text-headline text-label-primary">{title}</h3>
        <p className="text-subheadline text-label-secondary">{body}</p>
      </div>
    </div>
  );
};

/**
 * Figma "Store · App": an app's own page in the store. Cover and icon, what it does, what
 * buyers see of it, and what's good to know. `actions` come from the store, which owns
 * adding, managing and Notify me.
 */
const AppStoreAppPage = ({ app, copies, notified, spaceName, onBack, onAdd, onOpen, onManage, onToggleNotify, busy }) => {
  const { t } = useTranslation();
  const page = APP_STORE_PAGES[app.id];
  const key = `appStorePage.apps.${app.id}`;
  const text = (name) => t(`${key}.${name}`);
  const builtIn = app.isDefault && copies > 0;
  const singleCopy = SINGLE_COPY_APP_IDS.includes(app.id);

  const meta = [
    t(`appStorePage.${CATEGORY_KEYS[page.category]}`),
    app.comingSoon && t("appStorePage.soon"),
    builtIn && t("appStorePage.builtInEverySpace"),
    !app.comingSoon && !builtIn && page.sells && t("appStorePage.sellsAsProduct"),
    !builtIn && copies > 0 && t("appStorePage.inSpace", { count: copies }),
  ].filter(Boolean);

  const factValue = {
    shows: t("appStorePage.spaceMenu", { app: app.storeName }),
    who: text("who"),
    sell: t("appStorePage.yes"),
    status: t("appStorePage.soon"),
    costs: t("appStorePage.costsBudget"),
  };
  const factLabel = {
    shows: "factShowsIn",
    who: "factWhoSees",
    sell: "factSell",
    status: "factStatus",
    costs: "factCosts",
  };

  const actionClass = "grow lg:grow-0";
  const actions = app.comingSoon ? (
    <div className="flex w-full flex-col gap-space-8 lg:w-auto">
      <Button
        variant={notified ? "gray" : "filled"}
        loading={busy}
        onClick={onToggleNotify}
        fullWidth
        className="lg:w-auto"
      >
        <span className="hidden min-[600px]:inline">{notified ? text("notifyFull") : t("appStorePage.notify")}</span>
        <span className="min-[600px]:hidden">{notified ? t("appStorePage.notifyShort") : t("appStorePage.notify")}</span>
      </Button>
      {notified && <p className="text-caption-1 text-label-tertiary">{t("appStorePage.notifyNote")}</p>}
    </div>
  ) : (
    <div className="flex w-full items-center gap-space-8 lg:w-auto">
      {copies === 0 ? (
        <Button className={actionClass} onClick={onAdd}>
          {t("appStorePage.addToSpace")}
        </Button>
      ) : builtIn || singleCopy ? (
        <Button className={actionClass} onClick={onOpen}>
          {t("appStorePage.open")}
        </Button>
      ) : (
        <Button className={actionClass} onClick={onAdd}>
          {t("appStorePage.addAnother")}
        </Button>
      )}
      {copies > 0 && !builtIn && (
        <Button variant="gray" className={actionClass} onClick={onManage}>
          {t("appStorePage.manage")}
        </Button>
      )}
    </div>
  );

  return (
    <AppScope app={app.id} className="flex flex-col gap-space-20 lg:gap-[28px]">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-space-4 self-start text-subheadline-semibold text-label-secondary hover:text-label-primary"
      >
        <Icon name="chevron-left" size={16} />
        {t("appStorePage.title")}
      </button>

      <div className="relative pb-[36px] lg:pb-[52px]">
        <div className="relative h-[160px] overflow-hidden rounded-[24px] lg:h-[240px] lg:rounded-radius-xl">
          <AppCover app={app.id} size="phone" className="lg:hidden" />
          <AppCover app={app.id} size="hero" className="hidden lg:block" />
        </div>
        <span className="absolute left-space-20 top-[114px] rounded-[22.5px] shadow-[0_0_0_4px_var(--color-bg-base)] lg:hidden">
          <AppIcon app={app.id} size={80} glyph={32} radius={22.5} />
        </span>
        <span className="absolute left-space-40 top-[188.8px] hidden rounded-[28.8px] shadow-[0_0_0_6px_var(--color-bg-base)] lg:block">
          <AppIcon app={app.id} size={102.4} glyph={32} radius={28.8} />
        </span>
      </div>

      <div className="flex flex-col gap-space-16 lg:flex-row lg:items-center lg:gap-space-24">
        <div className="flex min-w-0 flex-1 flex-col gap-space-4">
          <h1 className="text-large-title text-label-primary">{app.storeName}</h1>
          <p className="text-subheadline text-label-secondary">{meta.join(" · ")}</p>
        </div>
        {actions}
      </div>

      <p className="max-w-[720px] text-body text-label-secondary">{text("pitch")}</p>

      <Divider />

      <section className="flex flex-col gap-space-16">
        <div className="flex items-center gap-space-8">
          <h2 className="min-w-0 flex-1 text-title-3 text-label-primary">{t("appStorePage.whatBuyersSee")}</h2>
          <span className="shrink-0 text-footnote text-label-tertiary">{text("style")}</span>
        </div>
        <div className="grid grid-cols-1 gap-space-16 min-[600px]:grid-cols-2 min-[600px]:gap-space-20 xl:grid-cols-3 xl:items-start xl:gap-space-24">
          <ProductPreview app={app} text={text} spaceName={spaceName} />
          <AfterPreview app={app} text={text} />
          <div className={`hidden flex-col gap-space-8 self-stretch rounded-radius-lg p-space-20 xl:flex ${APP_COLORS[appKey(app.id)].deep}`}>
            <Icon name="sparkle" size={20} className={APP_COLORS[appKey(app.id)].label} />
            <p className={`text-subheadline ${APP_COLORS[appKey(app.id)].label}`}>{text("note")}</p>
          </div>
        </div>
      </section>

      <div className="flex flex-col gap-space-20 lg:flex-row lg:items-start lg:gap-space-32">
        <section className="min-w-0 flex-1">
          <h2 className="mb-space-8 text-title-3 text-label-primary">{t("appStorePage.whatYouCanDo")}</h2>
          <div className="divide-y divide-separator-default">
            {page.features.map((icon, index) => (
              <Feature key={icon} app={app} icon={icon} title={text(`f${index + 1}Title`)} body={text(`f${index + 1}Body`)} />
            ))}
          </div>
        </section>

        <aside className="flex flex-col rounded-radius-lg bg-bg-raised px-space-20 py-space-8 lg:w-[340px] lg:shrink-0">
          <h2 className="py-space-12 text-headline text-label-primary">{t("appStorePage.goodToKnow")}</h2>
          {page.facts.map((fact) => (
            <div key={fact} className="flex flex-col gap-space-4 border-t border-separator-default py-space-12">
              <p className="text-footnote text-label-tertiary">{t(`appStorePage.${factLabel[fact]}`)}</p>
              <p className="text-subheadline-semibold text-label-primary">{factValue[fact]}</p>
            </div>
          ))}
        </aside>
      </div>
    </AppScope>
  );
};

export default AppStoreAppPage;
